import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Entity, EntityType, PaginationInfo, QueryFilter } from '../types';
import { resolveSourceColumns } from '../utils/resolveColumns';
import type { ResolvedEntityMap } from '../utils/resolveColumns';
import type { ColumnDef } from '../config/viewsConfig';
import { renderCellValue, getCellPlainValue } from '../utils/renderCellValue';
import { PaginationControls } from './PaginationControls';
import { ColumnPicker } from './ColumnPicker';
import { useEditMode } from '../context/EditModeContext';
import './EntityTable.css';
import './ConfirmDialog.css';

function defaultColumnWidth(col: ColumnDef): number {
  if (col.key === 'id') return 80;
  if (col.render === 'date') return 150;
  if (col.render === 'state') return 110;
  if (col.render === 'link') return 90;
  if (col.extract) return 90;
  return 200;
}

function PinIcon({ pinned }: { pinned: boolean }) {
  if (pinned) {
    return (
      <svg viewBox="0 0 16 16" fill="currentColor" stroke="none">
        <path d="M10.97 2.29a1 1 0 0 0-1.41 0L7.5 4.35 4.65 3.04a1 1 0 0 0-1.12.2L2.29 4.47a1 1 0 0 0 .2 1.56l2.06 1.15-2.48 2.48a.5.5 0 0 0 0 .71l.56.56a.5.5 0 0 0 .71 0l2.48-2.48 1.15 2.06a1 1 0 0 0 1.56.2l1.23-1.24a1 1 0 0 0 .2-1.12L8.65 6.5l2.06-2.06a1 1 0 0 0 0-1.41l-.74-.74z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.97 2.29a1 1 0 0 0-1.41 0L7.5 4.35 4.65 3.04a1 1 0 0 0-1.12.2L2.29 4.47a1 1 0 0 0 .2 1.56l2.06 1.15-2.48 2.48a.5.5 0 0 0 0 .71l.56.56a.5.5 0 0 0 .71 0l2.48-2.48 1.15 2.06a1 1 0 0 0 1.56.2l1.23-1.24a1 1 0 0 0 .2-1.12L8.65 6.5l2.06-2.06a1 1 0 0 0 0-1.41l-.74-.74z" />
    </svg>
  );
}

interface EntityTableProps {
  entities: Entity[];
  entityType?: EntityType;
  columns: ColumnDef[];
  defaultColumns?: ColumnDef[];
  /** Curated columns offered in the picker but not visible by default. */
  optionalColumns?: ColumnDef[];
  onColumnsChange?: (columns: ColumnDef[]) => void;
  resolvedEntities?: ResolvedEntityMap;
  pagination: PaginationInfo;
  onPageChange: (offset: number) => void;
  onLimitChange: (limit: number) => void;
  onEntityClick?: (entityId: number) => void;
  onSortChange?: (sortBy: string) => void;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  loading?: boolean;
  /** Override the filename stem for CSV export (e.g. 'nmr-tests'). */
  exportName?: string;
  /** When client-side search is active, the number of entities that matched. */
  filteredCount?: number;
  /** Raw entities for JSON key sampling in the column picker. Falls back to entities prop. */
  sampleEntities?: Entity[];
  /** Lab / view filters merged into column picker JSON discovery (same as EntityFilters). */
  extraFilters?: QueryFilter[];
  onDeleteEntity?: (entityId: number) => void;
  /** Fetch all entities for full CSV export. Called with a progress callback. */
  onExportAll?: (onProgress: (fetched: number, total: number) => void) => Promise<Entity[]>;
  /** Override the page-size dropdown options. Defaults to [25, 50, 100, 200]. */
  pageSizeOptions?: number[];
  /** Attribute key to group rows by (alternating tint per consecutive group). */
  groupByKey?: string;
}

export const EntityTable: React.FC<EntityTableProps> = ({
  entities,
  entityType,
  columns,
  defaultColumns,
  optionalColumns,
  onColumnsChange,
  resolvedEntities = new Map(),
  pagination,
  onPageChange,
  onLimitChange,
  onEntityClick,
  onSortChange,
  sortBy,
  sortOrder = 'asc',
  loading = false,
  exportName,
  filteredCount,
  sampleEntities,
  extraFilters,
  onDeleteEntity,
  onExportAll,
  pageSizeOptions = [25, 50, 100, 200],
  groupByKey,
}) => {
  const navigate = useNavigate();
  const { isEditMode } = useEditMode();
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ fetched: 0, total: 0 });

  // Column widths (resizable)
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const resizeRef = useRef<{
    key: string;
    startX: number;
    startWidth: number;
  } | null>(null);
  const didResizeRef = useRef(false);

  const getColWidth = useCallback((col: ColumnDef): number => {
    return columnWidths[col.key] || defaultColumnWidth(col);
  }, [columnWidths]);

  const handleResizeStart = useCallback((e: React.MouseEvent, col: ColumnDef) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = {
      key: col.key,
      startX: e.clientX,
      startWidth: getColWidth(col),
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [getColWidth]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeRef.current) return;
      const { key, startX, startWidth } = resizeRef.current;
      const newWidth = Math.max(50, startWidth + (e.clientX - startX));
      setColumnWidths(prev => ({ ...prev, [key]: newWidth }));
    };
    const handleMouseUp = () => {
      if (!resizeRef.current) return;
      didResizeRef.current = true;
      resizeRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      requestAnimationFrame(() => { didResizeRef.current = false; });
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Pinned columns (sticky)
  const storageKey = `pinned-cols:${entityType ?? 'default'}`;
  const [pinnedKeys, setPinnedKeys] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) return new Set(JSON.parse(stored));
    } catch { /* ignore */ }
    return new Set();
  });

  const togglePin = useCallback((key: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setPinnedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem(storageKey, JSON.stringify(Array.from(next))); } catch { /* ignore */ }
      return next;
    });
  }, [storageKey]);

  const pinnedOffsets = useMemo(() => {
    const offsets: Record<string, number> = {};
    let cumulative = 0;
    for (const col of columns) {
      if (pinnedKeys.has(col.key)) {
        offsets[col.key] = cumulative;
        cumulative += getColWidth(col);
      }
    }
    return offsets;
  }, [columns, pinnedKeys, getColWidth]);

  const lastPinnedKey = useMemo(() => {
    let last: string | null = null;
    for (const col of columns) {
      if (pinnedKeys.has(col.key)) last = col.key;
    }
    return last;
  }, [columns, pinnedKeys]);

  const sortedColumns = useMemo(() => {
    const pinned = columns.filter(c => pinnedKeys.has(c.key));
    const unpinned = columns.filter(c => !pinnedKeys.has(c.key));
    return [...pinned, ...unpinned];
  }, [columns, pinnedKeys]);

  const groupIndices = useMemo(() => {
    if (!groupByKey) return null;
    const indices: number[] = [];
    let currentGroup = 0;
    let lastVal: any = undefined;
    for (const entity of entities) {
      const val = entity.attributes?.[groupByKey];
      if (indices.length > 0 && val !== lastVal) currentGroup++;
      indices.push(currentGroup);
      lastVal = val;
    }
    return indices;
  }, [entities, groupByKey]);

  const getSortKey = (col: ColumnDef): string => col.key;

  const handleSortClick = (col: ColumnDef) => {
    if (didResizeRef.current) return;
    if (onSortChange) {
      onSortChange(getSortKey(col));
    }
  };

  const renderSortIndicator = (col: ColumnDef) => {
    if (sortBy !== getSortKey(col)) {
      return <span className="sort-indicator">↕</span>;
    }
    return (
      <span className={`sort-indicator sort-${sortOrder}`}>
        {sortOrder === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const downloadCsv = useCallback((data: Entity[], resolvedMap: ResolvedEntityMap) => {
    const escapeCsv = (val: string) => {
      if (val.includes(',') || val.includes('"') || val.includes('\n')) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    };

    const headerRow = columns.map(col => escapeCsv(col.label || col.key)).join(',');
    const dataRows = data.map(entity =>
      columns.map(col => escapeCsv(getCellPlainValue(entity, col, resolvedMap))).join(',')
    );

    const csv = [headerRow, ...dataRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stem = exportName || entityType || 'entities';
    a.download = `export-${stem}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [columns, entityType, exportName]);

  const handleExportClick = useCallback(() => {
    if (!onExportAll || pagination.count <= entities.length) {
      downloadCsv(entities, resolvedEntities);
      return;
    }
    setExportProgress({ fetched: 0, total: pagination.count });
    setExporting(false);
    setShowExportDialog(true);
  }, [onExportAll, pagination.count, entities, downloadCsv, resolvedEntities]);

  const handleExportConfirm = useCallback(async () => {
    if (!onExportAll) return;
    setExporting(true);
    try {
      const allEntities = await onExportAll((fetched, total) => {
        setExportProgress({ fetched, total });
      });
      let resolved: ResolvedEntityMap = resolvedEntities;
      if (entityType) {
        try {
          resolved = await resolveSourceColumns(allEntities as Entity[], columns, entityType);
        } catch {
          // fall back to existing map
        }
      }
      downloadCsv(allEntities as Entity[], resolved);
    } finally {
      setExporting(false);
      setShowExportDialog(false);
    }
  }, [onExportAll, downloadCsv, entityType, columns, resolvedEntities]);

  const tableWidth = useMemo(() => {
    return sortedColumns.reduce((sum, col) => sum + getColWidth(col), 0);
  }, [sortedColumns, getColWidth]);

  const cellOptions = { onEntityClick, onNavigate: navigate, resolvedEntities };

  if (loading) {
    return (
      <div className="table-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (entities.length === 0) {
    return (
      <div className="table-empty">
        <p>No entities found</p>
      </div>
    );
  }

  return (
    <div className="entity-table-container">
      <div className="table-controls">
        <div className="pagination-info">
          {filteredCount !== undefined
            ? `Showing ${filteredCount} of ${entities.length} on this page (${pagination.count} total)`
            : `Showing ${pagination.offset + 1}\u2013${Math.min(pagination.offset + pagination.limit, pagination.count)} of ${pagination.count}`
          }
        </div>
        <div className="table-controls-right">
          <div className="limit-control">
            <label>
              Per page:
              <select
                value={pagination.limit}
                onChange={(e) => onLimitChange(parseInt(e.target.value, 10))}
                className="limit-select"
              >
                {pageSizeOptions.map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
          </div>
          {entityType && defaultColumns && onColumnsChange && (
            <ColumnPicker
              entityType={entityType}
              defaultColumns={defaultColumns}
              optionalColumns={optionalColumns}
              visibleColumns={columns}
              onColumnsChange={onColumnsChange}
              entities={sampleEntities || entities}
              extraFilters={extraFilters}
            />
          )}
          <button className="column-picker-btn" onClick={handleExportClick} title="Export CSV">
            Export CSV
          </button>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="entity-table" style={{ width: tableWidth }}>
          <thead>
            <tr>
              {isEditMode && onDeleteEntity && <th className="actions-col"></th>}
              {sortedColumns.map(col => {
                const isSortable = col.sortable !== false && !col.extract;
                const isPinned = pinnedKeys.has(col.key);
                const isLastPinned = col.key === lastPinnedKey;
                const pinStyle: React.CSSProperties = isPinned
                  ? { left: pinnedOffsets[col.key], width: getColWidth(col) }
                  : { width: getColWidth(col) };
                return (
                  <th
                    key={col.key}
                    className={[
                      onSortChange && isSortable ? 'sortable' : '',
                      sortBy === getSortKey(col) ? 'sorted' : '',
                      isPinned ? 'pinned-col' : '',
                      isLastPinned ? 'pinned-col-last' : '',
                    ].filter(Boolean).join(' ')}
                    style={pinStyle}
                    onClick={onSortChange && isSortable ? () => handleSortClick(col) : undefined}
                  >
                    {col.label}
                    {onSortChange && isSortable && renderSortIndicator(col)}
                    <button
                      className={`pin-toggle${isPinned ? ' pinned' : ''}`}
                      onClick={(e) => togglePin(col.key, e)}
                      title={isPinned ? 'Unpin column' : 'Pin column'}
                    >
                      <PinIcon pinned={isPinned} />
                    </button>
                    <div
                      className={`col-resize-handle${resizeRef.current?.key === col.key ? ' resizing' : ''}`}
                      onMouseDown={(e) => handleResizeStart(e, col)}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {entities.map((entity, i) => (
              <tr key={entity.id}>
                {isEditMode && onDeleteEntity && (
                  <td className="actions-cell">
                    {confirmDeleteId === entity.id ? (
                      <span className="confirm-delete">
                        <button className="confirm-yes" onClick={() => { onDeleteEntity(entity.id!); setConfirmDeleteId(null); }}>Yes</button>
                        <button className="confirm-no" onClick={() => setConfirmDeleteId(null)}>No</button>
                      </span>
                    ) : (
                      <button className="delete-row-btn" title="Delete" onClick={() => setConfirmDeleteId(entity.id!)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                      </button>
                    )}
                  </td>
                )}
                {sortedColumns.map(col => {
                  const isPinned = pinnedKeys.has(col.key);
                  const isLastPinned = col.key === lastPinnedKey;
                  const isGroupKeyAlt = groupByKey && col.key === groupByKey && groupIndices && groupIndices[i] % 2 === 1;
                  const pinStyle: React.CSSProperties = isPinned
                    ? { left: pinnedOffsets[col.key], width: getColWidth(col) }
                    : { width: getColWidth(col) };
                  return (
                    <td
                      key={col.key}
                      className={[
                        isPinned ? 'pinned-col' : '',
                        isLastPinned ? 'pinned-col-last' : '',
                        isGroupKeyAlt ? 'group-key-alt' : '',
                      ].filter(Boolean).join(' ') || undefined}
                      style={pinStyle}
                    >
                      {renderCellValue(entity, col, cellOptions)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PaginationControls
        offset={pagination.offset}
        limit={pagination.limit}
        count={pagination.count}
        onPageChange={onPageChange}
        disabled={loading}
      />

      {showExportDialog && (
        <div className="confirm-dialog-overlay" onClick={!exporting ? () => setShowExportDialog(false) : undefined}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <h3 className="confirm-dialog-title">Export to CSV</h3>
            {!exporting ? (
              <>
                <p className="confirm-dialog-message">
                  Export all {pagination.count.toLocaleString()} rows to CSV? This will
                  export all matching data, not just the current page.
                </p>
                <div className="confirm-dialog-actions">
                  <button className="confirm-dialog-btn confirm-dialog-cancel" onClick={() => setShowExportDialog(false)}>
                    Cancel
                  </button>
                  <button className="confirm-dialog-btn confirm-dialog-confirm" onClick={handleExportConfirm}>
                    Export All
                  </button>
                </div>
              </>
            ) : (
              <div className="export-progress-container">
                <p className="confirm-dialog-message">
                  Exporting {exportProgress.fetched.toLocaleString()} of {exportProgress.total.toLocaleString()} rows...
                </p>
                <div className="export-progress-track">
                  <div
                    className="export-progress-fill"
                    style={{ width: `${exportProgress.total > 0 ? (exportProgress.fetched / exportProgress.total) * 100 : 0}%` }}
                  />
                </div>
                <span className="export-progress-pct">
                  {exportProgress.total > 0 ? Math.round((exportProgress.fetched / exportProgress.total) * 100) : 0}%
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
